import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, PlusCircle } from "lucide-react";

function EnvelopeIcon() {
  return (
    <div className="relative" style={{ width: 110, height: 90 }}>
      <svg
        width="110"
        height="90"
        viewBox="0 0 110 90"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <filter id="shadow" x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow
            dx="0"
            dy="4"
            stdDeviation="4"
            floodColor="#b0bfe8"
            floodOpacity="0.35"
          />
        </filter>

        <rect
          x="4"
          y="28"
          width="102"
          height="58"
          rx="8"
          fill="url(#bodyGrad)"
          filter="url(#shadow)"
        />

        <rect
          x="4"
          y="28"
          width="102"
          height="58"
          rx="8"
          fill="url(#innerGrad)"
        />

        <path
          d="M4 36 L55 62 L4 82"
          fill="url(#flapLeft)"
          opacity="0.55"
        />

        <path
          d="M106 36 L55 62 L106 82"
          fill="url(#flapRight)"
          opacity="0.45"
        />

        <path
          d="M4 36 Q4 28 12 28 L98 28 Q106 28 106 36 L55 8 Z"
          fill="url(#topFlapGrad)"
        />

        <path
          d="M4 36 L55 8 L106 36"
          stroke="#c5d3f0"
          strokeWidth="1"
          fill="none"
          opacity="0.6"
        />

        <rect
          x="30"
          y="52"
          width="50"
          height="4"
          rx="2"
          fill="white"
          opacity="0.35"
        />

        <rect
          x="36"
          y="61"
          width="38"
          height="4"
          rx="2"
          fill="white"
          opacity="0.25"
        />

        <defs>
          <linearGradient
            id="bodyGrad"
            x1="4"
            y1="28"
            x2="106"
            y2="86"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#dde6f8" />
            <stop offset="100%" stopColor="#c2d0f0" />
          </linearGradient>

          <linearGradient
            id="innerGrad"
            x1="4"
            y1="28"
            x2="106"
            y2="86"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#e8eefb" />
            <stop offset="100%" stopColor="#ccd8f5" />
          </linearGradient>

          <linearGradient
            id="flapLeft"
            x1="4"
            y1="36"
            x2="55"
            y2="82"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#b8c9ec" />
            <stop offset="100%" stopColor="#c8d5f0" />
          </linearGradient>

          <linearGradient
            id="flapRight"
            x1="106"
            y1="36"
            x2="55"
            y2="82"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#c0ceee" />
            <stop offset="100%" stopColor="#cdd8f2" />
          </linearGradient>

          <linearGradient
            id="topFlapGrad"
            x1="55"
            y1="8"
            x2="55"
            y2="36"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#d0dcf7" />
            <stop offset="100%" stopColor="#bfcdf2" />
          </linearGradient>
        </defs>
      </svg>

      {/* CHECK ICON */}
      <div
        className="absolute flex items-center justify-center rounded-full bg-green-500 shadow-lg"
        style={{
          width: 32,
          height: 32,
          top: -6,
          right: -4,
          boxShadow: "0 2px 8px rgba(34,197,94,0.4)",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 8L6.5 11.5L13 5"
            stroke="white"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

export default function RequestSuccess() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { uploadedFile, recipients, savedExpiry: savedExpiryRaw, signingOrder, requestId: serverRequestId } = state || {};
  const steps = ["Upload Document", "Add Recipients", "Set Verification Method", "Review & Send"];

  const requestId = React.useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const seq = String(
      Math.floor(Math.random() * 900) + 100
    ).padStart(6, "0");

    return `REQ-${y}-${m}-${d}-${seq}`;
  }, []);

  const sentOn = React.useMemo(() => {
    return new Date().toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  }, []);

  const expiresOn = React.useMemo(() => {
    const date = savedExpiryRaw
      ? new Date(savedExpiryRaw)
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [savedExpiryRaw]);

  const recipientCount = recipients?.length || 0;

  return (
    <div className="w-full bg-white mx-auto" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* STEPPER */}
      <div className="flex items-center justify-between relative mb-2" style={{ minHeight: "66px" }}>
        {steps.map((step, index) => (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== steps.length - 1 && (
              <div className="absolute top-5 left-1/2 w-full h-[2px] bg-green-500" />
            )}
            <div
              className="z-10 rounded-full flex items-center justify-center text-xs font-semibold bg-green-500 text-white"
              style={{ width: "40px", height: "40px" }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M2 6.5L5.2 9.75L11 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="mt-1 text-[11px] font-medium text-center text-green-600">{step}</p>
          </div>
        ))}
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-2xl mx-auto">

        {/* SUCCESS CARD */}
        <div className="border rounded-2xl shadow-sm overflow-hidden bg-white">

          {/* TOP — SUCCESS BANNER */}
          <div className="bg-green-50 border-b px-8 py-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full border-4 border-green-500 flex items-center justify-center mb-4">
              <EnvelopeIcon />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Request Sent Successfully!</h2>
            <p className="text-base font-normal text-gray-500 mt-1">
              Your signature request has been sent to{" "}
              <span className="font-semibold">{recipientCount}</span>{" "}
              recipient{recipientCount !== 1 ? "s" : ""}.
            </p>
          </div>

          {/* DETAILS */}
          <div className="px-8 py-6">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Request Details</h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Document Name</p>
                <p className="text-sm font-semibold text-gray-800">{uploadedFile?.name || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Request ID</p>
                <p className="text-sm font-semibold text-gray-800 font-mono">{serverRequestId || requestId}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Sent On</p>
                <p className="text-sm font-semibold text-gray-800">{sentOn}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Expires On</p>
                <p className="text-sm font-semibold text-gray-800">{expiresOn}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-400 mb-0.5">Recipients</p>
                <p className="text-sm font-semibold text-gray-800">{recipientCount} recipient{recipientCount !== 1 ? "s" : ""}</p>
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="px-8 pb-8 space-y-3 flex flex-col items-center">
            <p className="text-sm text-gray-500 text-center">{signingOrder
                ? "Recipients are notified one after another. You'll get a notification on the Request Status page as each step is completed."
                : "You'll get a notification on the Request Status page as each recipient completes their step."}</p>
            <div className="grid grid-cols-2 gap-3 w-full">
              <button
                onClick={() => navigate("/request-status", { state: { uploadedFile, recipients } })}
                className="border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition rounded-xl py-2.5 text-sm font-medium text-gray-700 flex items-center justify-center gap-2"
              >
                <Eye size={16} /> View Request Status
              </button>
              <button
                onClick={() => navigate("/request-signatures")}
                className="inline-flex items-center justify-center gap-2 h-[44px] px-6 bg-[#2563EB] hover:bg-[#1D4ED8] transition text-white font-semibold rounded-[10px] text-sm"
              >
                <PlusCircle size={15} /> Create Another
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}